import uibModalModule from 'angular-ui-bootstrap/src/modal';
import moment from 'moment';
import ngStorage from '../../../common/wrappers/ngstorage';

import MasterService from '../master.service';
import authServiceMod from '../../auth/auth.service';
import reminderModalMod from './reminder.modal';
import maintenanceModalMod from './maintenance.modal';
import maintenanceReminderModalMod from './maintenance_reminder.modal';
import APP_CONFIG from '../../app.config';
import {noop} from '../common.utils';
import keepaliveResourceMod from '../resources/keepalive.resource';
import hashServiceMod from '../hash.service';
import {parseQueryString} from '../query.utils';

/**
 * Warn the user this many minutes before the expiry timer.
 * @type {number}
 */
const REMIND_BEFORE = 5;

/**
 * Warn the user this many minutes before maintenance begins.
 * @type {number}
 */
const MAINTENANCE_REMIND_BEFORE = 15;

/**
 * Remember maintenance note dismissal for this many hours.
 *
 * We don't want to show the user the maintenance activity display over and over again. So remember that it was closed
 * for this many hours. It is stored in session storage so a new one will be shown anyway if the user opens a new
 * window.
 *
 * @type {number}
 */
const MAINTENANCE_REMEMBER = 1;

/**
 * Send a keepalive reset request this many seconds after a hash change.
 * @type {number}
 */
const HASH_CHANGE_KEEPALIVE_TIMER = 5;

/**
 * List of URL paths where keepalive timer should never run.
 * These paths are for new React SPAs.
 *
 * Strings should be URL regexes that will be matched against the current path.
 *
 * @type {Array.<string>}
 */
const NOT_KEEPALIVE_PATHS = [
  /\/omaoral/,
  /\/ajanvaraus/,
  /\/ajanvaraus-en/,
  /\/ajanvaraus-sv/,
];


/**
 * Key used for MasterService notifications to reset the keepalive timer. This should be used by other bundles via
 * `resetExpiry()` to trigger the reset.
 * @type {symbol}
 */
const KEEPALIVE_KEY = Symbol('keepalive');

/**
 * Key used for subscribing to expiry of the keepalive. This should be used by other bundles to listen to when the
 * keepalive timer has activated.
 * @type {symbol}
 */
const KEEPALIVE_EXPIRY_KEY = Symbol('keepalive-expiry');

/**
 * Key used by master bundle to subscribe to changes in the keepalive enabled state.
 * @type {symbol}
 */
const KEEPALIVE_ENABLED_KEY = Symbol('keepalive-enabled');

/**
 * Key used for subscribing to changes of the maintenance status.
 * @type {symbol}
 */
const MAINTENANCE_KEY = Symbol('maintenance');

/**
 * The keepalive service keeps track of requests to the backend and attempts to ensure that the user does not get
 * logged out of the backend without being notified on the frontend. When the keepalive timeout is being reached, the
 * service pops up a modal notifying the user.
 *
 * If the keepalive modal is not reacted to after the timeout, or if the user chooses "end session" in the modal, the
 * keepalive timer is expired. The user will be logged out and bundles will be notified of the expiration. The keepalive
 * functionality will then be disabled, until `sendKeepalive()` is called by some bundle, upon which time the keepalive
 * will start again. This means that bundles should be calling `sendKeepalive()` whenever the user is interacting with
 * the site, to ensure that keepalive is running but not expired while the user is active.
 *
 * The keepalive service also handles fetching the maintenance status of the application. The maintenance status starts
 * as null (not retrieved) and updates to either true or false (maintenance on or off). Apps listening to the
 * maintenance status should hold rendering until the value has been set to true or false to prevent flickering of
 * content.
 *
 * Because HTTP interceptors are not shared between apps on the page, each app should run the KeepaliveInterceptor to
 * notify the master application of activity that should reset the timer.
 */
class KeepaliveService extends MasterService {
  constructor(
    $log, $timeout, $sessionStorage,
    openReminderModal, openMaintenanceModal, openMaintenanceReminderModal,
    AuthService, KeepaliveResource
  ) {
    'ngInject';

    super();

    this.$log = $log;
    this.$timeout = $timeout;
    this.$sessionStorage = $sessionStorage;
    this.openReminderModal = openReminderModal;
    this.openMaintenanceModal = openMaintenanceModal;
    this.openMaintenanceReminderModal = openMaintenanceReminderModal;
    this.AuthService = AuthService;
    this.KeepaliveResource = KeepaliveResource;

    /**
     * Backend URL to call.
     * @type {string}
     */
    this.URL = `${APP_CONFIG.be}/keepalive/`;

    /**
     * Time in minutes that the user's session stays alive in the backend.
     * @type {?number}
     */
    this.keepalive = null;

    /**
     * Time of the next maintenance, if set.
     * @type {?Moment}
     */
    this.maintenanceAt = null;

    /**
     * Maintenance note to display to user, if any.
     * @type {?string}
     */
    this.maintenanceNote = null;

    /**
     * End time of maintenance, if given by backend.
     * @type {?Moment}
     */
    this.maintenanceEnd = null;

    this._addKey(KEEPALIVE_KEY);
    this._addKey(MAINTENANCE_KEY);
    this._addKey(KEEPALIVE_EXPIRY_KEY);
    this._addKey(KEEPALIVE_ENABLED_KEY);

    this._reminderModal = null;
    this._maintenanceReminderModal = null;

    this._reminderTimer = null;
    this._keepaliveTimer = null;
    this._maintenanceReminderTimer = null;
    this._maintenanceTimer = null;

    this._isEnabled = false;

    this._setValue(KEEPALIVE_EXPIRY_KEY, null);
    this._setValue(KEEPALIVE_ENABLED_KEY, this._isEnabled);
  }

  /**
   * Send a keepalive request to the backend, resetting the session expiry and retrieving the session lifetime and
   * maintenance information. Enables keepalive mode if it was disabled earlier.
   * @param {boolean} ignoreError Should failed keepalive sending be ignored? Default is false.
   * @return {Promise} Keepalive HTTP request promise
   */
  sendKeepalive({ignoreError} = {ignoreError: false}) {
    this._setValue(KEEPALIVE_ENABLED_KEY, true);
    let keepalivePromise = this.KeepaliveResource.sendKeepalive();
    keepalivePromise = ignoreError ? keepalivePromise.catch(noop) : keepalivePromise;
    return keepalivePromise;
  }

  /**
   * Reset the keepalive expiry timer, for example on some incoming request.
   */
  resetExpiry() {
    this._setValue(KEEPALIVE_KEY, true);
  }

  /**
   * Subscribe to be notified of changes in maintenance status (boolean true for maintenance mode or false for no
   * maintenance).
   * @param {function(boolean)} callback
   */
  subscribeMaintenance(callback) {
    this._subscribe(MAINTENANCE_KEY, callback);
  }

  /**
   * Subscribe to be notified of expiry of the keepalive timer. When the keepalive has expired or the user has chosen
   * to end the session, the user will be logged out, and then this notification will be sent. The value of the
   * notification is not relevant, except that on the first notify (when the listener is notified of the current value)
   * it may be null, in which case the timer has *not* expired. A subscribing bundle should reset its status
   * appropriately.
   * @param {function(boolean)} callback
   */
  subscribeKeepaliveExpiry(callback) {
    this._subscribe(KEEPALIVE_EXPIRY_KEY, callback);
  }

  // Retrieve the session lifetime (keepalive time) and maintenance information from the backend.
  _getKeepaliveData() {
    return this.KeepaliveResource.getMetadata()
    .then(({expire_timeout, next_maintenance, maintenance_note, maintenance_end}) => {
      if (expire_timeout) {
        this.keepalive = expire_timeout;
      }

      const query_params = parseQueryString();

      // Clear keepalive modal shown status if prompted
      if ('mclr' in query_params) {
        delete this.$sessionStorage.maintenance_warning_given;
        delete this.$sessionStorage.maintenance_notice_dismissed_at;
      }

      // If keepalive info is in query params, fake it to test things
      if ('mon' in query_params) {
        if (query_params.mon == null) {
          next_maintenance = moment();
        }
        else {
          next_maintenance = moment(query_params.mon);
        }

        if ('mnote' in query_params) {
          maintenance_note = query_params.mnote;
        }

        if ('mend' in query_params) {
          maintenance_end = moment(query_params.mend);
        }
      }
      else if ('moff' in query_params) {
        next_maintenance = null;
        maintenance_note = null;
      }

      if (next_maintenance) {
        this.maintenanceAt = next_maintenance;
        this.maintenanceNote = maintenance_note;
        this.maintenanceEnd = maintenance_end;
      }
    })
    .finally(() => {
      this._resetKeepaliveTimers();
      this._setMaintenanceTimers();
    });
  }

  // Set the keepalive timers, cancels both old timers.
  _resetKeepaliveTimers() {
    this._cancelTimers();

    if (!this._shouldRunKeepalive()) {
      this.$log.debug('Keepalive mode disabled, not starting timers again.');
    }
    else if (this.keepalive != null) {
      const remind_after = (this.keepalive - REMIND_BEFORE) * 60 * 1000;
      const keepalive_after = this.keepalive * 60 * 1000;

      this.$log.debug(`Reminding about keepalive after ${remind_after} milliseconds.`);

      this._reminderTimer = this.$timeout(() => this._showReminderModal(), remind_after);
      this._keepaliveTimer = this.$timeout(() => this._expireKeepalive(true), keepalive_after);
    }
    else {
      this.$log.debug('Keepalive length not fetched, not setting keepalive timer.');
    }
  }

  _setMaintenanceTimers() {
    if (this.maintenanceAt != null && this.maintenanceAt.isValid()) {
      const diff = Math.max(this.maintenanceAt.diff(moment(), 'minutes'), 0);
      const remind_at = Math.max(diff - MAINTENANCE_REMIND_BEFORE, 0);

      // If maintenance is already started, start without timeout
      if (diff === 0) {
        this.$log.debug('Activating maintenance immediately.');
        this._setMaintenanceStatus(true);
        this._showMaintenanceModal();
      }
      else {
        // Otherwise we can deduce that maintenance mode is *not* active right now, so set the proper status
        this._setMaintenanceStatus(false);

        this.$log.debug(`Maintenance activating in ${diff} minutes, reminding in ${remind_at} minutes.`);

        this._maintenanceReminderTimer = this.$timeout(() => this._showMaintenanceReminderModal(), remind_at * 60 * 1000);
        this._maintenanceTimer = this.$timeout(() => this._showMaintenanceModal(), diff * 60 * 1000);
      }
    }
    else {
      // If no maintenance is in sight, set status
      this._setMaintenanceStatus(false);
    }
  }

  // Subscribe to be notified of when the keepalive status is reset via resetExpiry().
  _subscribeKeepaliveReset(callback) {
    this._subscribe(KEEPALIVE_KEY, callback);
  }

  // Subscribe to be notified of when the keepalive enabled status is changed
  _subscribeEnabledStatus(callback) {
    this._subscribe(KEEPALIVE_ENABLED_KEY, callback);
  }

  _showMaintenanceModal() {
    // If reminder modal is still open, close it
    if (this._maintenanceReminderModal != null) {
      this._maintenanceReminderModal.dismiss();
    }

    // If maintenance modal has been recently dismissed, don't show it again
    if (!this._isMaintenanceDismissed()) {
      this.openMaintenanceModal(this.maintenanceNote).result.catch(noop).finally(() => {
        this._storeMaintenanceDismissed();
      });
    }
  }

  _showMaintenanceReminderModal() {
    // Don't show if reminder has just been shown
    if (this.$sessionStorage.maintenance_warning_given) {
      return;
    }

    this.$sessionStorage.maintenance_warning_given = true;
    this._maintenanceReminderModal = this.openMaintenanceReminderModal(this.maintenanceAt);
    this._maintenanceReminderModal.result
    .catch(noop)
    .finally(() => {
      this._maintenanceReminderModal = null;
    });
  }

  _showReminderModal() {
    if (!this._shouldRunKeepalive()) {
      this.$log.debug('Not opening keepalive reminder modal as prerequisites do not match.');
      return;
    }

    const expiry_at = moment().add(REMIND_BEFORE, 'minutes');
    this._reminderModal = this.openReminderModal(expiry_at);
    this._reminderModal.result
    .then(() => this.sendKeepalive())
    .catch(() => this._expireKeepalive())
    .finally(() => {
      this._reminderModal = null;
    });
  }

  // from_timer should be set to true if this function is called from the keepalive timer
  _expireKeepalive(from_timer=false) {
    if (!this._shouldRunKeepalive()) {
      this.$log.debug('Not expiring keepalive as prerequisites do not match.');
      return;
    }

    if (this._reminderModal != null) {
      this._reminderModal.dismiss();
    }

    if (this._maintenanceReminderModal != null) {
      this._maintenanceReminderModal.dismiss();
    }

    // Cancel timers if they were running to avoid double expiry, don't cancel keepalive timer if we were called by it,
    // so that we don't need to catch() in code listening to the keepalive timer (cancelling it throws "canceled")
    this._cancelTimers(!from_timer);

    // Disable keepalive until it's started again
    this._isEnabled = false;

    this.AuthService.logout().finally(() => {
      // After logout is complete, notify bundles of expiry
      this._setValue(KEEPALIVE_EXPIRY_KEY, true);
    });
  }

  // Save the maintenance modal dismiss status
  _storeMaintenanceDismissed() {
    this.$sessionStorage.maintenance_notice_dismissed_at = moment().format();
  }

  // Check if maintenance notice was dismissed recently
  _isMaintenanceDismissed() {
    if (this.$sessionStorage.maintenance_notice_dismissed_at == null) {
      return false;
    }

    const dismiss_date = moment(this.$sessionStorage.maintenance_notice_dismissed_at);
    return dismiss_date.isValid() && moment().diff(dismiss_date, 'hours') <= MAINTENANCE_REMEMBER;
  }

  // Should the keepalive be executed?
  _shouldRunKeepalive() {
    const url = window.location.href;
    return KeepaliveService._matchPath(url, NOT_KEEPALIVE_PATHS) ? false : this.AuthService.authenticated;
  }

  _setMaintenanceStatus(status) {
    this._setValue(MAINTENANCE_KEY, status);
  }

  _cancelTimers(cancel_keepalive=true) {
    if (this._reminderTimer != null) {
      this.$timeout.cancel(this._reminderTimer);
      this._reminderTimer = null;
    }

    if (this._keepaliveTimer != null && cancel_keepalive) {
      this.$timeout.cancel(this._keepaliveTimer);
      this._keepaliveTimer = null;
    }
  }

  static _matchPath(path, pathRegexs) {
    for (const regex of pathRegexs) {
      if (regex.test(path)) {
        return true;
      }
    }

    return false;
  }
}

function keepAliveServiceFactory($log, $timeout, $sessionStorage,
                                 openReminderModal, openMaintenanceModal, openMaintenanceReminderModal,
                                 AuthService, KeepaliveResource) {
  'ngInject';

  if (window.keepAliveService != null) {
    $log.debug("KeepAliveServiceFactory: already exists");
  }
  else {
    $log.debug("KeepAliveServiceFactory: creating new");
    window.keepAliveService = new KeepaliveService($log, $timeout, $sessionStorage,
      openReminderModal, openMaintenanceModal, openMaintenanceReminderModal,
      AuthService, KeepaliveResource);
  }

  return window.keepAliveService;
}

const mod = angular.module('app.common.keepaliveService', [
  uibModalModule,
  ngStorage,
  authServiceMod,
  reminderModalMod,
  maintenanceModalMod,
  maintenanceReminderModalMod,
  keepaliveResourceMod
])

.factory('KeepaliveService', keepAliveServiceFactory)

;

/**
 * Configure the keepalive interceptor that will notify the master bundle of all successful requests to the backend,
 * thereby resetting the keepalive timer. This should be run in every app that sends requests to the backend.
 */
function configureKeepaliveInterceptor($httpProvider) {
  'ngInject';

  $httpProvider.interceptors.push(function ($injector) {
    'ngInject';

    return {
      response(resp) {
        const KeepaliveService = $injector.get('KeepaliveService');

        if (resp.status >= 200 && resp.status < 300 && resp.config.url.startsWith(APP_CONFIG.be)) {
          KeepaliveService.resetExpiry();
        }

        return resp;
      }
    };
  });
}
configureKeepaliveInterceptor.imports = [mod.name];

/**
 * Listen for successful request notifications and reset the keepalive timers when they happen.
 */
function resetKeepaliveOnSuccessfulRequests($log, $timeout, KeepaliveService) {
  'ngInject';

  // Reset keepalive expiry whenever notifications of successful backend requests arrive
  KeepaliveService._subscribeKeepaliveReset(() => {
    $log.debug('Received request, resetting keepalive.');
    $timeout(() => KeepaliveService._resetKeepaliveTimers());
  });
}
resetKeepaliveOnSuccessfulRequests.imports = [mod.name];

/**
 * Listen for user authentication changes and reset keepalive timers when they happen.
 */
function resetKeepaliveOnUserAuthentication($log, $timeout, AuthService, KeepaliveService) {
  'ngInject';

  // Reset keepalive expiry whenever user's authentication status is changed
  AuthService.subscribeUser(() => {
    $timeout(() => {
      $log.debug('Authentication status changed, resetting keepalive.');

      if (AuthService.auth != null) {
        $log.debug('User authenticted, enabling keepalive mode.');
        KeepaliveService._isEnabled = true;
      }

      KeepaliveService._resetKeepaliveTimers();
    });
  });
}
resetKeepaliveOnUserAuthentication.imports = [authServiceMod, mod.name];

let _hash_change_keepalive_promise = null;

/**
 * Listen to hash changes and send keepalive reset as the user is obviously still on the site if the hash changes.
 */
function sendKeepaliveOnHashChanges($log, $rootScope, $timeout, KeepaliveService, HashService) {
  'ngInject';

  HashService.onHashChange($rootScope, () => {
    $log.debug('Hash changed, sending keepalive.');

    if (_hash_change_keepalive_promise != null) {
      $timeout.cancel(_hash_change_keepalive_promise);
    }

    _hash_change_keepalive_promise = $timeout(
      () => KeepaliveService.sendKeepalive({ignoreError: true}),
      HASH_CHANGE_KEEPALIVE_TIMER * 1000
    );
  });
}
sendKeepaliveOnHashChanges.imports = [mod.name, hashServiceMod];

/**
 * Fetch keepalive metadata and start the required timers.
 */
function fetchKeepaliveMetadata(KeepaliveService) {
  'ngInject';

  KeepaliveService._getKeepaliveData();
}
fetchKeepaliveMetadata.imports = [mod.name];

function listenToKeepaliveEnabledStatus($log, $timeout, KeepaliveService) {
  'ngInject';

  KeepaliveService._subscribeEnabledStatus(status => {
    $timeout(() => {
      $log.debug('Setting keepalive status to', status, 'according to subscription.');
      KeepaliveService._isEnabled = status;
    });
  });
}
listenToKeepaliveEnabledStatus.imports = [mod.name];

export {
  configureKeepaliveInterceptor,
  resetKeepaliveOnSuccessfulRequests,
  resetKeepaliveOnUserAuthentication,
  sendKeepaliveOnHashChanges,
  fetchKeepaliveMetadata,
  listenToKeepaliveEnabledStatus,
  REMIND_BEFORE,
  MAINTENANCE_REMIND_BEFORE,
  MAINTENANCE_REMEMBER,
};
export default mod.name;



// WEBPACK FOOTER //
// ./src/app/common/keepalive/keepalive.service.js